/**
 * RMS troubleshooting API client (stores, Shopify fetch proxies, admin uploads, bulk RMA status direct).
 */
// eslint-disable-next-line
import { injectable } from "inversify";
import axios from "axios";

import { IFileData } from "../../components/file-upload/file-upload";

import { TYPE, rootContainer } from "../container/root";
import { IApiResponse, IHttpService } from "../http-service";
import { uuid } from "../helpers";

/** to be DEPRECATED start, used only in troubleshooting page */
interface IObjectEnvelope<T> {
    data: T;
}
  
export interface IListEnvelope<T> {
    data: T[];
    pagination: IPagination;
}

export interface IPagination {
    per_page: number;
    current_page: number;
    total: number;
}
/** to be DEPRECATED end, used only in troubleshooting page */

/** Allowed `target_status` values for POST /rms-api/troubleshooting/update-rma-status-direct */
export const DIRECT_RMA_STATUS_VALUES = [
    'pending_approval',
    'approved',
    'shipment_received',
    'done',
] as const;

export type DirectRmaStatus = typeof DIRECT_RMA_STATUS_VALUES[number];

/** Allowed `stale_entity_mode` values for POST /rms-api/troubleshooting/duplicate-store */
export const STALE_ENTITY_MODE_VALUES = [
    'soft_delete',
    'disable',
    'leave',
] as const;

export type StaleEntityMode = typeof STALE_ENTITY_MODE_VALUES[number];

/** Possible `status` values of a store copy (Duplicate Store) attempt. */
export const STORE_COPY_STATUS_VALUES = [
    'pending',
    'processing',
    'completed',
    'failed',
] as const;

export type StoreCopyStatus = typeof STORE_COPY_STATUS_VALUES[number];

/** Possible `action` values recorded for an individual store copy action. */
export const STORE_COPY_ACTION_VALUES = [
    'created',
    'updated',
    'integration_linked',
    'integration_not_found',
    'disabled',
    'soft_deleted',
] as const;

export type StoreCopyActionType = typeof STORE_COPY_ACTION_VALUES[number];

/** A source/target store as returned inside a store copy attempt. */
export interface IStoreCopyStore {
    id: number;
    name: string | null;
    display_name: string | null;
}

/** One recorded change performed during a store copy attempt. */
export interface IStoreCopyAction {
    id: number;
    entity_type: string;
    source_id: number | null;
    new_id: number | null;
    action: StoreCopyActionType | string;
    message: string | null;
    before_json: Record<string, any> | null;
    after_json: Record<string, any> | null;
    created_at: string | null;
}

/** A single "Duplicate Store" run with its actions and resolved stores. */
export interface IStoreCopyAttempt {
    id: number;
    status: StoreCopyStatus | string;
    stale_entity_mode: StaleEntityMode | string;
    failure_reason: string | null;
    triggered_by_user_id: number | null;
    super_admin_action_id: number | null;
    started_at: string | null;
    finished_at: string | null;
    created_at: string | null;
    from_store: IStoreCopyStore | null;
    to_store: IStoreCopyStore | null;
    actions: IStoreCopyAction[];
}

/** Optional filters for GET /rms-api/troubleshooting/store-copy-attempts. */
export interface IStoreCopyAttemptsFilters {
    toUserId?: string | number;
    fromUserId?: string | number;
    /** Free-text search over store name, internal id, or Global-e user id (source or target). */
    storeSearch?: string;
    status?: StoreCopyStatus;
    page?: number;
    perPage?: number;
}

const LOG_GROUP_KEYS = [
    "PHP",
    "WORKER",
    "SQS_WORKER",
    "SHIPPING",
    "SHIPPING_WORKER",
    "INTEGRATOR",
    "INTEGRATOR_WORKER",
    "SQS_INTEGRATOR_WORKER",
    "BIFROST",
    "PORTAL_BFF",
    "RMS_API",
    "API",
    "PDF",
];

export interface IStore {
    id: number,
    name: string,
    display_name?: string,
}

export interface ITroubleshootingService {
    getStoresList: () => Promise<IStore[]>,
    getShopifyCustomer: (storeId: string, customerId: string) => Promise<any>,
    getDiscountCode: (storeId: string, discountCode: string) => Promise<any>,
    getOrder: (storeId: string, orderName: string) => Promise<any>,
    getOrderById: (storeId: string, orderName: string) => Promise<any>,
    getOrderByRMAId: (storeId: string, rmaId: string) => Promise<any>,
    getDraftOrder: (storeId: string, orderName: string) => Promise<any>,
    getDraftOrderById: (storeId: string, orderId: string) => Promise<any>,
    getProduct: (storeId: string, productId: string) => Promise<any>,
    getVariants: (storeId: string, variantIds: string) => Promise<any>,
    getFulfillmentById: (storeId: string, fulfillmentId: string) => Promise<any>,
    getRmaById: (storeId: string, rmaId: string) => Promise<any>,
    getOrderEligibility: (storeId: string, orderName: string) => Promise<any>,
    getOrderEligibilityLegacy: (storeId: string, orderName: string) => Promise<any>,
    bulkUpdateRmaStatus: (storeId: string, rmaIDs: string, status: number) => Promise<any>,
    bulkUpdateRmaStatusDirectly: (storeId: string | number, rmaIdsCommaSeparated: string, targetStatus: DirectRmaStatus) => Promise<any>,
    createSecondaryPortal: (storeId: string, portalName: string) => Promise<any>,
    fetchSuperAdminActions: (storeId: string) => Promise<any>,
    downloadTranslations: () => Promise<string>,
    uploadTranslationsFile: (data: IFileData) => Promise<any>,
    clearTranslationsCache: (storeId: string) => Promise<any>,
    copyStoreSettings: (storeId: string | number, copySettingsStore: string | number) => Promise<any>,
    duplicateStore: (storeId: string | number, copySettingsStore: string | number, staleEntityMode?: StaleEntityMode) => Promise<any>,
    getStoreCopyAttempts: (filters?: IStoreCopyAttemptsFilters) => Promise<IListEnvelope<IStoreCopyAttempt>>,
    transferRejectedRmaToCanceled: (storeId: string, rmaIds: string) => Promise<any>,
    getStoreFeatures: (storeId: string) => Promise<any>,
    activateFeatureForShop: (storeId: string, featureId: number) => Promise<any>,
    deactivateFeatureForShop: (storeId: string, featureId: number) => Promise<any>,
    setFeatureQuota: (storeId: string, featureId: number, quota: number) => Promise<any>,
    generateLogsPageUrl: (startTime: string, endTime: string, filters: Record<string, string>) => Promise<any>,
    getErrorDistribution: (isResolved?: boolean, days?: number) => Promise<any>,
    getErrorRatio: (days?: number) => Promise<any>,
    changeStoreOwner: (storeId: string, newOwnerEmail: string) => Promise<any>,
    fetchRefundCalculationResults: (storeId: string|number, rmaId: string|number) => Promise<any>,
    getProfileExplanations: (storeId: string | number, capability: string) => Promise<any>,
    removeUserFromStore: (storeId: string|number, userEmail: string) => Promise<any>,
    bulkAutoRefundRmas: (storeId: string|number, rmaIds: string, skipPolicyRuleCheck: boolean) => Promise<any>,
    createPlatformStore: (data: {
        platform: string;
        credentials: Record<string, any>;
        email: string;
        firstname: string;
        surname: string;
    }) => Promise<any>,
    validateEmail: (storeId: string | number) => Promise<any>,
    createAdministrators: (users: ICreateAdministratorUser[]) => Promise<any>,
    uploadTroubleshootingCsv: (storeId: string | number, file: File) => Promise<ITroubleshootingCsvUploadResponse>,
}

export interface ICreateAdministratorUser {
    email: string;
    first_name: string;
    last_name: string;
    display_name: string;
    viewer?: boolean;
    editor?: boolean;
}

export interface ICreateAdministratorUserResultRow {
    email: string;
    message: string;
}

export interface ICreateAdministratorsResult {
    status: boolean;
    message: string;
    created: string[];
    skipped: string[];
    failed: string[];
    user_results?: ICreateAdministratorUserResultRow[];
}

export interface ITroubleshootingCsvUploadResponse {
    data: {
        url: string;
        expiresAt: string;
    };
}

@injectable()
class TroubleshootingService implements ITroubleshootingService {
    private readonly httpService: IHttpService = rootContainer.get<IHttpService>(TYPE.ApiHttp);

    async getStoresList (): Promise<IStore[]> {
        const res = await this.httpService.post<IStore[]>(`/rms-api/troubleshooting/stores`);
        return res;
    }

    async getShopifyCustomer (storeId: string|number, customerId: string): Promise<any> {
        const res = await this.httpService.get<IObjectEnvelope<any>>(`/rms-api/troubleshooting/get-customer/${storeId}/${encodeURIComponent(customerId)}`);
        return res;
    }

    async getDiscountCode (storeId: string|number, discountCode: string): Promise<any> {
        const res = await this.httpService.get<IObjectEnvelope<any>>(`/rms-api/troubleshooting/get-discount/${storeId}/${encodeURIComponent(discountCode)}`);
        return res;
    };

    async getOrder (storeId: string|number, orderName: string): Promise<any> {
        const res = await this.httpService.get<IObjectEnvelope<any>>(`/rms-api/troubleshooting/get-order/${storeId}/${encodeURIComponent(orderName)}`);
        return res;
    };

    async getOrderById (storeId: string|number, orderName: string): Promise<any> {
        const res = await this.httpService.get<IObjectEnvelope<any>>(`/rms-api/troubleshooting/get-order-by-id/${storeId}/${encodeURIComponent(orderName)}`);
        return res;
    };

    async getOrderByRMAId (storeId: string|number, rmaId: string): Promise<any> {
        const res = await this.httpService.get<IObjectEnvelope<any>>(`/rms-api/troubleshooting/get-order-by-rma-id/${storeId}/${encodeURIComponent(rmaId)}`);
        return res;
    };

    async getDraftOrder (storeId: string|number, orderName: string): Promise<any> {
        const res = await this.httpService.get<IObjectEnvelope<any>>(`/rms-api/troubleshooting/get-draftorder/${storeId}/${encodeURIComponent(orderName)}`);
        return res;
    };

    async getDraftOrderById (storeId: string|number, orderId: string): Promise<any> {
        const res = await this.httpService.get<IObjectEnvelope<any>>(`/rms-api/troubleshooting/get-draftorder-by-id/${storeId}/${encodeURIComponent(orderId)}`);
        return res;
    };

    async getProduct (storeId: string|number, productId: string): Promise<any> {
        const res = await this.httpService.get<IListEnvelope<any>>(`/rms-api/troubleshooting/get-product/${storeId}/${encodeURIComponent(productId)}`);
        return res;
    };

    async getVariants (storeId: string|number, variantIds: string): Promise<any> {
        const res = await this.httpService.get<IListEnvelope<any>>(`/rms-api/troubleshooting/get-variants/${storeId}/${encodeURIComponent(variantIds)}`);
        return res;
    };

    async getFulfillmentById (storeId: string|number, fulfillmentId: string|number): Promise<any> {
        const res = await this.httpService.get<IObjectEnvelope<any>>(`/rms-api/troubleshooting/get-fulfillment-by-id/${storeId}/${fulfillmentId}`);
        return res;
    };

    async getRmaById (storeId: string|number, rmaId: string|number): Promise<any> {
        const res = await this.httpService.get<IObjectEnvelope<any>>(`/rms-api/troubleshooting/get-rma-by-id/${storeId}/${rmaId}`);
        return res;
    };

    async getOrderEligibility (storeId: string|number, orderName: string): Promise<any> {
        const res = await this.httpService.get<IObjectEnvelope<any>>(`/rms-api/troubleshooting/get-order-eligibility/${storeId}`, {
            orderName: encodeURIComponent(orderName),
        });
        return res;
    }

    async getOrderEligibilityLegacy (storeId: string|number, orderName: string): Promise<any> {
        const res = await this.httpService.get<IObjectEnvelope<any>>(`/rms-api/troubleshooting/get-order-eligibility-legacy/${storeId}`, {
            orderName: encodeURIComponent(orderName),
        });
        return res;
    }

    async bulkUpdateRmaStatus (storeId: string|number, rmaIDs: string, status: number, skipPolicyRuleCheck?: string): Promise<any> {
        const res = await this.httpService.post<IObjectEnvelope<any>>(`/rms-api/troubleshooting/update-rma-status/${storeId}`, {
            rma_ids: rmaIDs.trim().split(',').map(id => id.trim()),
            rma_status: status,
            status_done_skip_policy_rule_check: skipPolicyRuleCheck,
        });
        return res;
    }

    async bulkUpdateRmaStatusDirectly (
        storeId: string | number,
        rmaIdsCommaSeparated: string,
        targetStatus: DirectRmaStatus,
    ): Promise<any> {
        const rma_ids = rmaIdsCommaSeparated.trim().split(',').map((id) => id.trim()).filter((id) => id.length > 0);
        const res = await this.httpService.post<IObjectEnvelope<any>>('/rms-api/troubleshooting/update-rma-status-direct', {
            store_id: storeId,
            rma_ids,
            target_status: targetStatus,
        });
        return res;
    }

    async createSecondaryPortal (storeId: string|number, portalName: string): Promise<any> {
        const res = await this.httpService.post<IObjectEnvelope<any>>(`/rms-api/troubleshooting/secondary-portal/${storeId}`, {
            portal_name: portalName.trim(),
        });
        return res;
    }

    async fetchSuperAdminActions (storeId: string|number): Promise<any> {
        const res = await this.httpService.get<IListEnvelope<any>>(`/rms-api/troubleshooting/get-superadmin-actions/${storeId}`);
        return res;
    }

    async downloadTranslations(): Promise<string> {
        const res = await this.httpService.get<IApiResponse<string>>('/rms-api/troubleshooting/admin-locales/download');
        return res.payload;
    }

    async uploadTranslationsFile(data: IFileData): Promise<any> {
        const res = await this.httpService.post<IApiResponse<any>>('/rms-api/troubleshooting/admin-locales/upload', data);
        return res;
    }

    async clearTranslationsCache (storeId: string|number): Promise<any> {
        const res = await this.httpService.post<IObjectEnvelope<any>>(`/rms-api/troubleshooting/clear-translations-cache/${storeId}`);
        return res;
    }

    async copyStoreSettings (storeId: string|number, storeIdOrName: string|number): Promise<any> {
        const target = typeof storeIdOrName === 'string' ? storeIdOrName.trim() : storeIdOrName;
        const res = await this.httpService.post<IObjectEnvelope<any>>(`/rms-api/troubleshooting/copy-store-settings/${storeId}`, {
            store_id_or_name: target,
        });
        return res;
    }

    async duplicateStore (storeId: string|number, storeIdOrName: string|number, staleEntityMode?: StaleEntityMode): Promise<any> {
        const target = typeof storeIdOrName === 'string' ? storeIdOrName.trim() : storeIdOrName;
        const res = await this.httpService.post<IObjectEnvelope<any>>(`/rms-api/troubleshooting/duplicate-store/${storeId}`, {
            store_id_or_name: target,
            ...(staleEntityMode && { stale_entity_mode: staleEntityMode }),
        });
        return res;
    }

    async getStoreCopyAttempts (filters: IStoreCopyAttemptsFilters = {}): Promise<IListEnvelope<IStoreCopyAttempt>> {
        const queryParams: Record<string, any> = {};
        if (filters.toUserId != null) queryParams.to_user_id = filters.toUserId;
        if (filters.fromUserId != null) queryParams.from_user_id = filters.fromUserId;
        if (filters.storeSearch) queryParams.store_search = filters.storeSearch;
        if (filters.status) queryParams.status = filters.status;
        if (filters.page) queryParams.page = filters.page;
        if (filters.perPage) queryParams.per_page = filters.perPage;
        const res = await this.httpService.get<IListEnvelope<IStoreCopyAttempt>>(
            '/rms-api/troubleshooting/store-copy-attempts',
            queryParams,
        );
        return res;
    }

    async transferRejectedRmaToCanceled (storeId: string|number, rmaIds: string): Promise<any> {
        const res = await this.httpService.post<IObjectEnvelope<any>>(`/rms-api/troubleshooting/soft-cancel-rma/${storeId}`, {
            rma_ids: rmaIds.trim().split(',').map(id => id.trim()),
        });
        return res;
    }

    async getStoreFeatures (storeId: string|number): Promise<any> {
        const res = await this.httpService.get<IObjectEnvelope<any>>(`/rms-api/troubleshooting/get-shop-active-features/${storeId}?include_inactive=true`);
        return res;
    }

    async activateFeatureForShop (storeId: string|number, featureId: number): Promise<any> {
        const res = await this.httpService.post<IObjectEnvelope<any>>(`/rms-api/troubleshooting/activate-feature/${storeId}`, {
            feature_id: featureId,
        });
        return res;
    }

    async deactivateFeatureForShop (storeId: string|number, featureId: number): Promise<any> {
        const res = await this.httpService.post<IObjectEnvelope<any>>(`/rms-api/troubleshooting/deactivate-feature/${storeId}`, {
            feature_id: featureId,
        });
        return res;
    }

    async setFeatureQuota (storeId: string|number, featureId: number, quota: number): Promise<any> {
        const res = await this.httpService.post<IObjectEnvelope<any>>(`/rms-api/troubleshooting/set-feature-quota/${storeId}`, {
            feature_id: featureId,
            quota: quota,
        });
        return res;
    }

    async generateLogsPageUrl (startTime: string, endTime: string, filters: Record<string, string>): Promise<any> {
        const params = new URLSearchParams();
        
        // Add log_group_keys parameter
        params.append('log_group_keys', LOG_GROUP_KEYS.join(','));
        
        // Add start_time and end_time parameters
        params.append('start_time', startTime);
        params.append('end_time', endTime);
        
        // Add filters parameter as JSON string
        params.append('filters', JSON.stringify(filters));
        
        // Add output parameter
        params.append('output', 'html');
        
        return `${this.httpService.getBaseUrl() ?? ''}/rms-api/troubleshooting/generate-log-url?${params.toString()}`;
    }

    async getErrorDistribution (isResolved?: boolean, days?: number): Promise<any> {
        let url = `/rms-api/errors/get-error-distribution`;
        const params = new URLSearchParams();
        
        if (isResolved !== undefined) {
            params.append('isResolved', isResolved.toString());
        }
        if (days !== undefined) {
            params.append('days', days.toString());
        }
        
        if (params.toString()) {
            url += `?${params.toString()}`;
        }
        
        const res = await this.httpService.get<IObjectEnvelope<any>>(url);
        return res;
    }

    async getErrorRatio (days?: number): Promise<any> {
        let url = `/rms-api/errors/get-error-ratio`;
        if (days !== undefined) {
            url += `?days=${days}`;
        }
        const res = await this.httpService.get<IObjectEnvelope<any>>(url);
        return res;
    }

    async changeStoreOwner (storeId: string|number, newOwnerEmail: string): Promise<any> {
        const res = await this.httpService.post<IObjectEnvelope<any>>(`/rms-api/troubleshooting/change-store-owner/${storeId}`, {
            new_owner_email: newOwnerEmail.trim(),
        });
        return res;
    }

    async fetchRefundCalculationResults (storeId: string|number, rmaId: string|number): Promise<any> {
        const res = await this.httpService.get<IObjectEnvelope<any>>(`/rms-api/troubleshooting/get-refund-calculation-results/${storeId}/${rmaId}`);
        return res;
    }

    async getProfileExplanations (storeId: string | number, capability: string): Promise<any> {
        const res = await this.httpService.get<IObjectEnvelope<any>>(
            `/rms-api/troubleshooting/explain-profiles/${storeId}`,
            { capability }
        );
        return res;
    }

    async removeUserFromStore (storeId: string|number, userEmail: string): Promise<any> {
        const res = await this.httpService.post<IObjectEnvelope<any>>(`/rms-api/troubleshooting/remove-user-from-store/${storeId}`, {
            user_email: userEmail.trim(),
        });
        return res;
    }

    async bulkAutoRefundRmas (storeId: string|number, rmaIds: string, skipPolicyRuleCheck: boolean = false): Promise<any> {
        const res = await this.httpService.post<IObjectEnvelope<any>>(`/rms-api/troubleshooting/auto-refund-rma/${storeId}`, {
            rma_ids: rmaIds.trim().split(',').map(id => id.trim().replace("RMA", "")),
            skip_policy_rule_check: skipPolicyRuleCheck,
        });
        return res;
    }

    async createPlatformStore (data: {
        platform: string;
        credentials: Record<string, any>;
        email: string;
        firstname: string;
        surname: string;
    }): Promise<any> {
        const res = await this.httpService.post<IObjectEnvelope<any>>(`/rms-api/troubleshooting/create-platform-store`, {
            platform: data.platform,
            credentials: data.credentials,
            email: data.email,
            firstname: data.firstname,
            surname: data.surname,
        });
        return res;
    }

    async validateEmail (storeId: string | number): Promise<any> {
        const res = await this.httpService.post<IObjectEnvelope<any>>(`/rms-api/troubleshooting/validate-email`, {});
        return res;
    }

    async createAdministrators (users: ICreateAdministratorUser[]): Promise<any> {
        const res = await this.httpService.post<IObjectEnvelope<ICreateAdministratorsResult>>(
            '/rms-api/troubleshooting/create-administrators',
            { users }
        );
        return res;
    }

    async uploadTroubleshootingCsv (storeId: string | number, file: File): Promise<ITroubleshootingCsvUploadResponse> {
        const formData = new FormData();
        formData.append('file', file);
        const baseUrl = this.httpService.getBaseUrl() ?? '';
        const requestId = `RMS_${uuid().replace(/-/g, '')}_${Date.now()}`;
        const res = await axios.post<ITroubleshootingCsvUploadResponse>(
            `${baseUrl}/rms-api/troubleshooting/upload-csv`,
            formData,
            {
                withCredentials: true,
                headers: {
                    Accept: 'application/json',
                    'X-Request-Id': requestId,
                },
            }
        );
        return res.data;
    }
}

export default TroubleshootingService;
